const LANGUAGE_NAMES = {
  'de-de': 'German',
  'en-us': 'English',
  'en-gb': 'English',
  'fr-fr': 'French',
  'fi-fi': 'Finnish',
  'nl-nl': 'Dutch',
  'it-it': 'Italian',
  'ja-jp': 'Japanese',
  'pl-pl': 'Polish',
  'sv-se': 'Swedish',
  'el-gr': 'Greek',
  'pt-pt': 'Portuguese',
  'es-es': 'Spanish',
  'tr-tr': 'Turkish',
  'ar-arab': 'Arabic'
}

export const SYSTEM_PROMPT_CACHE_KEY = 'pixfit-ai-system-prompt-v13'

export const getSystemPromptFingerprint = (
  availableFilters,
  maxNumberOfAddedExercises,
  locale: string = 'en-gb',
  exerciseLibrarySummary: any = null
): string => {
  return JSON.stringify({
    availableFilters,
    maxNumberOfAddedExercises,
    locale,
    exerciseLibrarySummary
  })
}

export const getCachedSystemPrompt = (fingerprint?: string): string | null => {
  let cachedPrompt: string | null = null
  try {
    cachedPrompt = window.localStorage.getItem(SYSTEM_PROMPT_CACHE_KEY)
  } catch (e) {
    return null
  }
  if (!cachedPrompt || !cachedPrompt.trim().length) return null

  try {
    const cached = JSON.parse(cachedPrompt)
    if (!fingerprint || cached.fingerprint === fingerprint) return cached.prompt || null
    return null
  } catch (e) {
    return fingerprint ? null : cachedPrompt
  }
}

export const setCachedSystemPrompt = (prompt: string, fingerprint?: string): void => {
  try {
    window.localStorage.setItem(SYSTEM_PROMPT_CACHE_KEY, JSON.stringify({ prompt, fingerprint }))
  } catch (e) {
    // Ignore storage failures; the generated prompt can be rebuilt on demand.
  }
}

export const clearCachedSystemPrompt = (): void => {
  try {
    window.localStorage.removeItem(SYSTEM_PROMPT_CACHE_KEY)
  } catch (e) {
    // Ignore storage failures; clearing a cache is best-effort.
  }
}

export const getSystemPrompt = (
  availableFilters,
  maxNumberOfAddedExercises,
  locale: string = 'en-gb',
  exerciseLibrarySummary: any = null
) => {
  const languageName = LANGUAGE_NAMES[locale] || 'English'
  const librarySummaryText = exerciseLibrarySummary
    ? `\nLibrary count map: ${JSON.stringify(exerciseLibrarySummary)}.\n`
    : ''

  return `
# PixFit AI
Trainer-facing workout-builder assistant. All user-facing text MUST be in ${languageName} (locale: ${locale}); keep it short and practical.

Tools: ask_question, apply_filter_tags, undo_tags, build_workout_from_filtered, remove_exercises, remove_all_exercises, set_workout_name, check_current_workout, close_chat.

Rules:
- Questions after the greeting MUST use ask_question; never ask plain-text questions. One ask_question per response, then stop.
- App appends localized "Any"; do not add an Any/no-preference option yourself.
- Required intake order: duration, Category, Execution, Joint/Muscle Group/Clinical Picture, then equipment. Skip known fields; "15 minutes kids workout" already gives duration and Category.
- Optional/narrowing questions are capped at two. Required missing fields and required Range of Motion clarification may still be asked one at a time.
- Never ask difficulty, level, or phase. Do not ask Movement Pattern, Focus, Body Area, or Range of Motion unless explicitly requested, except required range ambiguity.
- Equipment question is yes/no only. No equipment => no_equipment. Equipment allowed without a named item => requires_equipment true. Named equipment => use only that equipment.
- If a tool reports requires_range_of_motion_selection, ask_question with required_range_of_motion true and exact range_of_motion_options. If trainer chooses Any, do not apply every range value or ask again.
- Use only available filter values. Available filters: ${JSON.stringify(availableFilters)}.
- Control filters and duration only. Never select, request, infer, or expose exercise IDs. App code selects exercises, pairs sides, and keeps timing. Do not edit repetitions or tempo.
- Build flow: apply intended tags, then build_workout_from_filtered with a concise descriptive workout_name based on the request. Never use "Untitled Workout" as a workout_name. If count is 0 or build reports filtered_pool_exhausted, relax one optional filter and retry. If no relaxable optional filter remains and repeat_fill_possible, ask whether to broaden filters or allow repeated exercises. Use allow_repeated_exercises true only after consent.
- Never relax health limitations, explicit equipment restrictions, or core intent. Large filtered pools are good; build without narrowing.
- For remove/delete/take out, use check_current_workout with exercise details only when positions/names are needed, then remove_exercises. Named exercise requests are filter intent; do not guarantee a specific exercise.
- Health limitations are filter-only. No medical advice/disclaimers. Do not suggest unsupported actions like save/share/export/print/email/rest-period changes.
- After changes, give a short declarative confirmation and required equipment summary. Do not end with a question.
${librarySummaryText}
`
}
